import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, DollarSign, Briefcase, TrendingUp, TrendingDown, 
  Activity, Star, Calendar, BarChart3, Settings, Plus,
  UserCheck, Clock, Award, AlertCircle, CheckCircle, FileText
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { companyService, type CompanyStats, type CompanyMember } from '@/services/companyService';
import { useGlobalToast } from '@/contexts/ToastContext';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { SkipLink } from '@/components/ui/SkipLink';
import { FadeIn } from '@/components/ui/AnimatedComponents';

export function CompanyDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success, error } = useGlobalToast();
  
  const [stats, setStats] = useState<CompanyStats | null>(null);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [recentJobs, setRecentJobs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFabMenu, setShowFabMenu] = useState(false);

  const isCompanyOwner = companyService.isCompanyOwner(user as any);

  useEffect(() => {
    if (!isCompanyOwner) {
      navigate('/dashboard');
      return;
    }
    
    fetchDashboardData();
  }, [user?.id, isCompanyOwner, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showFabMenu && !target.closest('.fab-container')) {
        setShowFabMenu(false);
      }
    };

    if (showFabMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFabMenu]);

  const fetchDashboardData = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    try {
      const [statsData, membersData] = await Promise.all([
        companyService.getCompanyStats(user.id),
        companyService.getCompanyMembers(user.id)
      ]);

      console.log('✅ Dashboard data fetched:', { statsData, membersCount: membersData.length });
      setStats(statsData);
      setMembers(membersData);
    } catch (err: any) {
      console.error('❌ Failed to fetch dashboard data:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.status,
        response: err.response
      });
      
      error('Unable to Load Dashboard', `Could not load company dashboard data. ${err.message || 'Please refresh the page'}`);
      
      // Set empty data so the page still renders
      setStats({
        totalMembers: 0,
        activeMembers: 0,
        totalJobsCompleted: 0,
        totalRevenue: 0,
        averageTeamRating: 0,
        monthlyRevenue: 0,
        monthlyJobs: 0
      });
      setMembers([]);
    } finally {
      console.log('✅ Dashboard data fetch complete, setting isLoading to false');
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <SkipLink href="#main-content">Skip to main content</SkipLink>
        <main id="main-content" role="main" className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading company dashboard...</p>
        </main>
      </div>
    );
  }

  if (!isCompanyOwner) return null;

  const activeMembers = members.filter(m => m.isActive);
  const earningsTrend = stats && stats.monthlyRevenue && stats.totalRevenue 
    ? ((stats.monthlyRevenue / stats.totalRevenue) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20 md:pb-8">
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      
      <main id="main-content" role="main" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Company Dashboard' }]} />
        
        {/* Welcome Header */}
        <div className="mb-4 sm:mb-6 mt-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Company Dashboard
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your team and track company performance</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Revenue */}
          <FadeIn delay={0}>
            <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <DollarSign className="w-6 h-6 text-primary-600" />
                </div>
                {earningsTrend > 0 && (
                  <Badge variant="success" size="sm">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {earningsTrend.toFixed(0)}%
                  </Badge>
                )}
              </div>
              <div className="text-3xl font-black text-gray-900 mb-1">
                €{stats?.totalRevenue?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600">Total Revenue</div>
              <div className="text-xs text-gray-500 mt-1">
                €{stats?.monthlyRevenue?.toLocaleString() || '0'} this month
              </div>
            </Card>
          </FadeIn>

          {/* Team Members */}
          <FadeIn delay={50}>
            <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="text-3xl font-black text-gray-900 mb-1">
                {stats?.totalMembers || 0}
              </div>
              <div className="text-sm text-gray-600">Team Members</div>
              <div className="text-xs text-gray-500 mt-1">
                {stats?.activeMembers || 0} active today
              </div>
            </Card>
          </FadeIn>

          {/* Jobs Completed */}
          <FadeIn delay={100}>
            <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <div className="text-3xl font-black text-gray-900 mb-1">
                {stats?.totalJobsCompleted || 0}
              </div>
              <div className="text-sm text-gray-600">Jobs Completed</div>
              <div className="text-xs text-gray-500 mt-1">
                {stats?.monthlyJobs || 0} this month
              </div>
            </Card>
          </FadeIn>

          {/* Average Rating */}
          <FadeIn delay={150}>
            <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <Star className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              <div className="text-3xl font-black text-gray-900 mb-1">
                {stats?.averageTeamRating?.toFixed(1) || '0.0'}
              </div>
              <div className="text-sm text-gray-600">Team Rating</div>
              <div className="text-xs text-gray-500 mt-1">
                Across all members
              </div>
            </Card>
          </FadeIn>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Team Members Card */}
            <Card>
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-primary-600" />
                  <h2 className="text-lg font-bold text-gray-900">Team Members</h2>
                  <Badge variant="success" size="sm">{activeMembers.length} Active</Badge>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate('/team')}>
                    Manage Team
                  </Button>
                  <Button size="sm" onClick={() => navigate('/company/invitations')} icon={<Plus className="w-4 h-4" />}>
                    Invite
                  </Button>
                </div>
              </div>

              <div className="p-6">
                {members.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No team members yet</p>
                    <Button onClick={() => navigate('/company/invitations')}>
                      Invite Your First Member
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {members.slice(0, 6).map((member) => (
                      <div
                        key={member.id}
                        className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
                        onClick={() => navigate(`/team/member/${member.id}`)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-bold">
                              {member.firstName[0]}{member.lastName[0]}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">
                                {member.firstName} {member.lastName}
                              </div>
                              <div className="text-xs text-gray-500 capitalize">{member.role}</div>
                            </div>
                          </div>
                          <Badge variant={member.isActive ? 'success' : 'neutral'} size="sm">
                            {member.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">€{member.hourlyRate}/hr</span>
                          <span className="text-gray-500 capitalize">{member.employmentType}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {members.length > 6 && (
                  <div className="mt-4 text-center">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/team')}>
                      View All {members.length} Members
                    </Button>
                  </div>
                )}
              </div>
            </Card>

            {/* Recent Activity */}
            <Card>
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-bold text-gray-900">Recent Activity</h2>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/jobs')}>
                  View All
                </Button>
              </div>

              <div className="p-6">
                {recentJobs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No recent activity</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentJobs.map((job) => (
                      <div key={job.id} className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{job.title}</div>
                            <div className="text-sm text-gray-500 mt-1">
                              Assigned to {job.assignedTo}
                            </div>
                          </div>
                          <Badge variant="success" size="sm">{job.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Performance Tips */}
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <div className="p-6">
                <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                  💡 Company Tips
                </h3>
                <div className="space-y-3 text-sm text-blue-800">
                  <div className="bg-white/60 p-3 rounded-lg">
                    <p className="font-medium mb-1">Grow Your Team</p>
                    <p className="text-xs">Invite skilled professionals to handle more jobs and increase revenue.</p>
                  </div>
                  <div className="bg-white/60 p-3 rounded-lg">
                    <p className="font-medium mb-1">Track Performance</p>
                    <p className="text-xs">Monitor individual member performance to identify top performers.</p>
                  </div>
                  <div className="bg-white/60 p-3 rounded-lg">
                    <p className="font-medium mb-1">Set Clear Roles</p>
                    <p className="text-xs">Define responsibilities and permissions for each team member.</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Floating Action Button */}
      <div className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-30">
        <div className="relative group fab-container">
          <button
            className="w-14 h-14 bg-primary-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-700 transition-all hover:scale-110 min-h-touch min-w-touch"
            onClick={() => setShowFabMenu(!showFabMenu)}
            aria-label="Quick actions menu"
            aria-expanded={showFabMenu}
          >
            <span className="text-2xl font-bold" aria-hidden="true">+</span>
          </button>

          {showFabMenu && (
            <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 w-52">
              <button
                onClick={() => {
                  navigate('/team');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm min-h-touch"
                aria-label="Manage team"
              >
                <Users className="w-4 h-4 text-primary-600" />
                <span>Manage Team</span>
              </button>
              <button
                onClick={() => {
                  navigate('/company/invitations');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <Plus className="w-4 h-4 text-emerald-600" />
                <span>Invite Members</span>
              </button>
              <button
                onClick={() => {
                  navigate('/calendar');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm"
              >
                <Calendar className="w-4 h-4 text-blue-600" />
                <span>Team Calendar</span>
              </button>
              <button
                onClick={() => {
                  navigate('/analytics');
                  setShowFabMenu(false);
                }}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-sm border-t border-gray-100"
              >
                <BarChart3 className="w-4 h-4 text-purple-600" />
                <span>Analytics</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around items-center py-2 px-4">
          <button
            onClick={() => navigate('/company/dashboard')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg bg-primary-50 text-primary-600"
          >
            <Users className="w-5 h-5" />
            <span className="text-xs font-medium">Dashboard</span>
          </button>
          <button
            onClick={() => navigate('/team')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <UserCheck className="w-5 h-5" />
            <span className="text-xs">Team</span>
          </button>
          <button
            onClick={() => navigate('/company/invitations')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <Plus className="w-5 h-5" />
            <span className="text-xs">Invite</span>
          </button>
          <button
            onClick={() => navigate('/analytics')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          >
            <BarChart3 className="w-5 h-5" />
            <span className="text-xs">Analytics</span>
          </button>
        </div>
      </div>
    </div>
  );
}
